import * as Yup from "yup";
import { dateToyyyyMMdd } from "../../../util/date";
import { getPatchOperations } from "../../../util/form";

export const personSanktionForm = (config: {
  personId: string;
  personSanktionId?: string;
}): FormConfig<PersonSanktionDTO> => {
  const { personId, personSanktionId } = config;
  return {
    title: "sanktion",
    validationSchema: Yup.object({
      sagsNr: Yup.string().required("Sagsnr. skal være sat"),
      naevnskendelseDato: Yup.date()
        .nullable()
        .required("Kendelsesdato skal være sat"),
      naevnskendelseSanktionstypeNr: Yup.number().required(
        "Sanktion skal være sat",
      ),
      naevnskendelseBoedebeloeb: Yup.string()
        .test(
          "naevnskendelseBoedebeloeb",
          "Bødebeløb skal indtastes uden punktummer og kommaer",
          function (naevnskendelseBoedebeloeb) {
            return (
              !naevnskendelseBoedebeloeb ||
              (!naevnskendelseBoedebeloeb.includes(",") &&
                !naevnskendelseBoedebeloeb.includes("."))
            );
          },
        )
        .test(
          "naevnskendelseBoedebeloeb",
          "Bødebeløb skal være et tal",
          function (naevnskendelseBoedebeloeb) {
            return (
              !naevnskendelseBoedebeloeb ||
              /^-?[0-9]*$/.test(naevnskendelseBoedebeloeb)
            );
          },
        )
        .test(
          "naevnskendelseBoedebeloeb",
          "Bødebeløb skal være minimum 0",
          function (naevnskendelseBoedebeloeb) {
            if (!naevnskendelseBoedebeloeb) return true;
            return Number(naevnskendelseBoedebeloeb) >= 0;
          },
        ),
      indbragtretten: Yup.boolean(),
      retskendelseDato: Yup.date()
        .nullable()
        .test(
          "retskendelseDato",
          'Domsdato kan ikke være sat, uden at Indbragt er "Ja"',
          function (retskendelseDato) {
            return !(retskendelseDato && !this.parent.indbragtretten);
          },
        ),
      retskendelseSanktionstypeNr: Yup.string().test(
        "retskendelseSanktionstypeNr",
        'Sanktion (retten) kan ikke være sat, uden at Indbragt er "Ja"',
        function (retskendelseSanktionstypeNr) {
          return !(retskendelseSanktionstypeNr && !this.parent.indbragtretten);
        },
      ),
      retskendelseBoedebeloeb: Yup.string()
        .test(
          "retskendelseBoedebeloeb",
          "Bødebeløb skal indtastes uden punktummer og kommaer",
          function (retskendelseBoedebeloeb) {
            return (
              !retskendelseBoedebeloeb ||
              (!retskendelseBoedebeloeb.includes(",") &&
                !retskendelseBoedebeloeb.includes("."))
            );
          },
        )
        .test(
          "retskendelseBoedebeloeb",
          "Bødebeløb skal være et tal",
          function (retskendelseBoedebeloeb) {
            return (
              !retskendelseBoedebeloeb ||
              /^-?[0-9]*$/.test(retskendelseBoedebeloeb)
            );
          },
        )
        .test(
          "retskendelseBoedebeloeb",
          "Bødebeløb skal være minimum 0",
          function (retskendelseBoedebeloeb) {
            if (!retskendelseBoedebeloeb) return true;
            return Number(retskendelseBoedebeloeb) >= 0;
          },
        )
        .test(
          "retskendelseBoedebeloeb",
          'Bødebeløb (retten) kan ikke være sat, uden at Indbragt er "Ja"',
          function (retskendelseBoedebeloeb) {
            return !(retskendelseBoedebeloeb && !this.parent.indbragtretten);
          },
        )
        .test(
          "retskendelseBoedebeloeb",
          "Bødebeløb (retten) kan ikke være sat, uden at domsdato er sat",
          function (retskendelseBoedebeloeb) {
            return !(retskendelseBoedebeloeb && !this.parent.retskendelseDato);
          },
        ),
      bemaerkninger: Yup.string(),
      sagsLink: Yup.string(),
    }),
    lockedValues: [
      {
        id: "personSanktionId",
        value: personSanktionId,
      },
      {
        id: "personId",
        value: personId,
      },
    ],
    form: [
      {
        component: "textinput",
        id: "sagsNr",
        label: "Sagsnr.",
        initialValue: "",
        isRequired: true,
      },
      {
        component: "datepicker",
        id: "naevnskendelseDato",
        label: "Kendelsesdato (nævn)",
        initialValue: "",
        isRequired: true,
      },
      {
        component: "kode-select",
        kodeListeType: "Sanktionstype",
        id: "naevnskendelseSanktionstypeNr",
        label: "Sanktion (nævn)",
        placeholder: "Vælg sanktionstype",
        initialValue: "",
        excludeDeaktiveret: true,
      },
      {
        component: "textinput",
        id: "naevnskendelseBoedebeloeb",
        label: "Bødebeløb (nævn)",
        initialValue: "",
        placeholder: "f.eks. 10000",
      },
      {
        component: "textarea",
        id: "bemaerkninger",
        label: "Bemærkninger",
        initialValue: "",
      },
      {
        component: "textinput",
        id: "sagsLink",
        initialValue: "",
        label: "Kendelse",
      },
      {
        component: "textinput",
        id: "afgoerelseskoder",
        label: "Afgørelseskoder",
        initialValue: "",
      },
      {
        component: "boolean",
        id: "indbragtretten",
        label: "Indbragt",
        initialValue: false,
      },
      {
        component: "datepicker",
        id: "retskendelseDato",
        label: "Domsdato",
        initialValue: "",
        conditionalHidden: ({ method }) => {
          if (method === "post") {
            return true;
          }
          return false;
        },
      },
      {
        component: "kode-select",
        kodeListeType: "Sanktionstype",
        id: "retskendelseSanktionstypeNr",
        label: "Sanktion (retten)",
        placeholder: "Vælg sanktionstype",
        initialValue: "",
        excludeDeaktiveret: true,
        conditionalHidden: ({ method }) => {
          if (method === "post") {
            return true;
          }
          return false;
        },
      },
      {
        component: "textinput",
        id: "retskendelseBoedebeloeb",
        label: "Bødebeløb (retten)",
        placeholder: "f.eks. 10000",
        initialValue: "",
        conditionalHidden: ({ method }) => {
          if (method === "post") {
            return true;
          }
          return false;
        },
      },
    ],
    get: `person/${personId}/sanktioner/${personSanktionId}`,
    post(values: PersonSanktionDTO) {
      const {
        naevnskendelseBoedebeloeb,
        naevnskendelseDato,
        retskendelseBoedebeloeb,
        retskendelseDato,
        retskendelseSanktionstypeNr,
      } = values;
      const body = {
        ...values,
        naevnskendelseDato: dateToyyyyMMdd(naevnskendelseDato),
        naevnskendelseBoedebeloeb: naevnskendelseBoedebeloeb
          ? naevnskendelseBoedebeloeb
          : null,
        retskendelseSanktionstypeNr: retskendelseSanktionstypeNr
          ? retskendelseSanktionstypeNr
          : null,
        retskendelseDato: retskendelseDato
          ? dateToyyyyMMdd(retskendelseDato)
          : null,
        retskendelseBoedebeloeb: retskendelseBoedebeloeb
          ? retskendelseBoedebeloeb
          : null,
      };
      return {
        body,
        query: `person/${personId}/sanktioner/`,
      };
    },
    patch(values: Partial<PersonSanktionDTO>) {
      return {
        query: `person/${personId}/sanktioner/${personSanktionId}`,
        body: JSON.stringify(getPatchOperations(values)),
      };
    },
    delete() {
      return {
        query: `person/${personId}/sanktioner/${personSanktionId}`,
      };
    },
  };
};
